import { useSelector } from "react-redux"
import { createSelector } from "@reduxjs/toolkit"
import EModuleIds, { ESubModuleIds } from "enums/ModuleEnum"
import { TStore } from "store/Store"
import { IAccess } from "pages/Common/commonTypes"

// ---------------------------------------------------------------------------
// PERMISSIONS registry
//
// Each entry: { module: <module_id>, title: "<exact backend title>" }
// For Operations sub-modules add:  submodule: <submodule_id>
//
// To add a new permission → append one line in the right section.
// The key prefix mirrors the module name so identical titles stay unique
// (e.g. every module has "View Tickets" but PROJECTS__VIEW_TICKETS ≠ SURVEY__VIEW_TICKETS).
// ---------------------------------------------------------------------------
export const PERMISSIONS = {
    // ── Purchase Orders (module_id: 24) ──────────────────────────────────────
    // Payable payment terms: Add is the first version, Edit every later one.
    // The server checks the same two on save (purchase_orders_v3/terms_views.py).
    // Dhanesh's RBAC list (2026-09-30); added by scripts/po_module_permissions.py.
    PURCHASE_ORDERS__VIEW_ALL_TABS: { module: EModuleIds.PURCHASE_ORDERS, title: "View All Tabs" },
    PURCHASE_ORDERS__VIEW_RECEIVABLE_POS: { module: EModuleIds.PURCHASE_ORDERS, title: "View Receivable POs" },
    PURCHASE_ORDERS__VIEW_PAYABLE_POS: { module: EModuleIds.PURCHASE_ORDERS, title: "View Payable POs" },
    PURCHASE_ORDERS__ADD_RECEIVABLE_PO: { module: EModuleIds.PURCHASE_ORDERS, title: "Add Receivable PO" },
    PURCHASE_ORDERS__EDIT_STATUS: { module: EModuleIds.PURCHASE_ORDERS, title: "Edit Status" },
    PURCHASE_ORDERS__DELETE_PO: { module: EModuleIds.PURCHASE_ORDERS, title: "Delete PO" },
    PURCHASE_ORDERS__EDIT_SO_ITEMS: { module: EModuleIds.PURCHASE_ORDERS, title: "Edit SO Items" },
    PURCHASE_ORDERS__EDIT_DETAILS: { module: EModuleIds.PURCHASE_ORDERS, title: "Edit Details" },
    PURCHASE_ORDERS__ADD_PAYMENT_TERM: { module: EModuleIds.PURCHASE_ORDERS, title: "Add Payment Term" },
    PURCHASE_ORDERS__EDIT_PAYMENT_TERM: { module: EModuleIds.PURCHASE_ORDERS, title: "Edit Payment Term" },

    // ── Projects (module_id: 7) ──────────────────────────────────────────────
    PROJECTS__VIEW_TICKETS: { module: EModuleIds.PROJECTS, title: "View Tickets" },
    PROJECTS__VIEW_COMMENTS: { module: EModuleIds.PROJECTS, title: "View Comments" },
    PROJECTS__VIEW_TICKET_HISTORY: { module: EModuleIds.PROJECTS, title: "View Ticket History" },
    PROJECTS__ADD_TICKETS: { module: EModuleIds.PROJECTS, title: "Add Tickets" },
    PROJECTS__ADD_COMMENTS: { module: EModuleIds.PROJECTS, title: "Add Comments" },
    PROJECTS__EDIT_TICKET_DETAILS: { module: EModuleIds.PROJECTS, title: "Edit Ticket Details" },
    PROJECTS__DROP_TICKETS: { module: EModuleIds.PROJECTS, title: "Drop tickets" },
    PROJECTS__UPDATE_TICKET_STATUS: { module: EModuleIds.PROJECTS, title: "Update ticket status" },
    PROJECTS__REASSIGN_TICKET: { module: EModuleIds.PROJECTS, title: "Reassign ticket" },
    PROJECTS__ADD_TAG: { module: EModuleIds.PROJECTS, title: "Add Tag" },
    PROJECTS__VIEW_TAG: { module: EModuleIds.PROJECTS, title: "View Tag" },
    PROJECTS__DELETE_TAG: { module: EModuleIds.PROJECTS, title: "Delete Tag" },
    PROJECTS__MAP_TAG: { module: EModuleIds.PROJECTS, title: "Map Tag" },
    PROJECTS__UNMAP_TAG: { module: EModuleIds.PROJECTS, title: "Unmap Tag" },
    PROJECTS__MARK_READY_FOR_DISPATCH: { module: EModuleIds.PROJECTS, title: "Mark Ready for dispatch" },
    PROJECTS__VIEW_DOWNLOAD: { module: EModuleIds.PROJECTS, title: "View Download" },
    PROJECTS__VIEW_LINKED_TICKETS: { module: EModuleIds.PROJECTS, title: "View Linked Tickets" },
    PROJECTS__VIEW_SALES_TAB: { module: EModuleIds.PROJECTS, title: "View Sales Tab" },
    PROJECTS__VIEW_INFRA_TAB: { module: EModuleIds.PROJECTS, title: "View Infra Tab" },
    PROJECTS__VIEW_ONBOARDING_TAB: { module: EModuleIds.PROJECTS, title: "View Onboarding Tab" },
    PROJECTS__VIEW_PROJECT_BUDGET: { module: EModuleIds.PROJECTS, title: "View Project Budget" },
    PROJECTS__VIEW_DOC_HUB: { module: EModuleIds.PROJECTS, title: "View Doc Hub" },
    PROJECTS__VIEW_HISTORY: { module: EModuleIds.PROJECTS, title: "View History" },
    PROJECTS__EDIT_ON_HOLD: { module: EModuleIds.PROJECTS, title: "Edit on-hold" },
    PROJECTS__EDIT_STAKEHOLDER: { module: EModuleIds.PROJECTS, title: "Edit Stakeholder" },
    PROJECTS__EDIT_MATERIAL: { module: EModuleIds.PROJECTS, title: "Edit Material" },
    PROJECTS__EDIT_SALES_TAB: { module: EModuleIds.PROJECTS, title: "Edit Sales Tab" },
    PROJECTS__EDIT_INFRA_TAB: { module: EModuleIds.PROJECTS, title: "Edit Infra Tab" },
    PROJECTS__EDIT_ONBOARDING_TAB: { module: EModuleIds.PROJECTS, title: "Edit Onboarding Tab" },
    PROJECTS__EDIT_PROJECT_BUDGET: { module: EModuleIds.PROJECTS, title: "Edit Project Budget" },
    PROJECTS__EDIT_DOC_HUB: { module: EModuleIds.PROJECTS, title: "Edit Doc Hub" },
    PROJECTS__EDIT_PROJECT_CATEGORY: { module: EModuleIds.PROJECTS, title: "Edit Project Category" },
    PROJECTS__ADD_CHANGE_ORDER: { module: EModuleIds.PROJECTS, title: "Add Change Order" },
    PROJECTS__ADD_ACCOUNT: { module: EModuleIds.PROJECTS, title: "Add Account" },
    PROJECTS__ADD_PO: { module: EModuleIds.PROJECTS, title: "Add PO" },
    PROJECTS__ADD_PLP: { module: EModuleIds.PROJECTS, title: "Add PLP" },
    PROJECTS__ADD_BUDGET: { module: EModuleIds.PROJECTS, title: "Add Budget" },
    PROJECTS__ADD_DOC: { module: EModuleIds.PROJECTS, title: "Add Doc" },
    PROJECTS__ADD_PROJECT_CATEGORY: { module: EModuleIds.PROJECTS, title: "Add Project Category" },
    PROJECTS__TRANSFER_OWNERSHIP: { module: EModuleIds.PROJECTS, title: "Transfer Ownership" },

    // ── Installation (module_id: 2, submodule_id: 1) ─────────────────────────
    INSTALLATION__VIEW_TICKETS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Tickets",
    },
    INSTALLATION__VIEW_COMMENTS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Comments",
    },
    INSTALLATION__VIEW_TICKET_HISTORY: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Ticket History",
    },
    INSTALLATION__ADD_COMMENTS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add Comments",
    },
    INSTALLATION__EDIT_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Edit Ticket",
    },
    INSTALLATION__UPDATE_TICKET_STATUS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Update ticket status",
    },
    INSTALLATION__REASSIGN_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Reassign ticket",
    },
    INSTALLATION__BLOCK_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Block Ticket",
    },
    INSTALLATION__REQUEST_NEW_SURVEY: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Request New Survey",
    },
    INSTALLATION__EDIT_CHARGER_DETAILS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Edit Charger Details",
    },
    INSTALLATION__ADD_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "Add Tag" },
    INSTALLATION__VIEW_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "View Tag" },
    INSTALLATION__DELETE_TAG: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Delete Tag",
    },
    INSTALLATION__MAP_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "Map Tag" },
    INSTALLATION__UNMAP_TAG: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Unmap Tag",
    },
    INSTALLATION__MARK_READY_FOR_DISPATCH: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Mark Ready for dispatch",
    },
    INSTALLATION__VIEW_DOWNLOAD: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Download",
    },
    INSTALLATION__VIEW_LINKED_TICKETS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Linked Tickets",
    },
    INSTALLATION__VIEW_CONTRACTOR_MGMT: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Contractor Management",
    },
    INSTALLATION__VIEW_MATERIAL_DETAILS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Material Details",
    },
    INSTALLATION__VIEW_SITE_DETAIL: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Site Detail",
    },
    INSTALLATION__VIEW_PROJECT_ESTIMATE: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Project Estimate",
    },
    INSTALLATION__VIEW_DOC_HUB: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Doc Hub",
    },
    INSTALLATION__VIEW_APPROVAL_TAB: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Approval Tab",
    },
    INSTALLATION__VIEW_HISTORY: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View History",
    },
    INSTALLATION__EDIT_DROP_STATUS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Edit Drop Status",
    },
    INSTALLATION__EDIT_TARGET_DATE: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Edit Target Date",
    },
    INSTALLATION__EDIT_CONTRACTOR_STATUS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Edit Contractor status",
    },
    INSTALLATION__EDIT_BOQ: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "Edit BOQ" },
    INSTALLATION__EDIT_GRN: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "Edit GRN" },
    INSTALLATION__EDIT_PURCHASE_ORDER: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Edit Purchase Order",
    },
    INSTALLATION__EDIT_BILLS_AND_PAYMENTS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Edit Bills & Payments",
    },
    INSTALLATION__EDIT_BULK_STATUS_UPDATE: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Edit Bulk status Update",
    },
    INSTALLATION__EDIT_DR: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "Edit DR" },
    INSTALLATION__EDIT_REVERSE_LOGISTICS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Edit Reverse Logistics",
    },
    INSTALLATION__EDIT_DOC: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "Edit Doc" },
    INSTALLATION__ADD_APPROVAL: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add Approval",
    },
    INSTALLATION__ADD_SURVEY: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add Survey",
    },
    INSTALLATION__ADD_EV_APPLICATION: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add EV Application",
    },
    INSTALLATION__ADD_INFRA_SCOPE_OF_WORK: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add Infra Scope of Work",
    },
    INSTALLATION__ADD_CHANGE_ORDER_REQUEST: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add Change Order Request",
    },
    INSTALLATION__ADD_NEW_CONTRACTOR: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add New Contractor",
    },
    INSTALLATION__ADD_BOQ: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "Add BOQ" },
    INSTALLATION__ADD_GRN: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "Add GRN" },
    INSTALLATION__ADD_PURCHASE_ORDER: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add Purchase Order",
    },
    INSTALLATION__ADD_BILLS_AND_PAYMENTS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add Bills & Payments",
    },
    INSTALLATION__ADD_DR: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "Add DR" },
    INSTALLATION__ADD_REVERSE_LOGISTICS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add Reverse Logistics",
    },
    INSTALLATION__ADD_UPLOAD_GRN: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add Upload GRN",
    },
    INSTALLATION__ADD_DOC: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.INSTALLATION, title: "Add Doc" },

    // Stage strip / task list (CRIS-3813). VIEW shows the strip at all; ADD is the first
    // "Add Stages" on a ticket with no list; EDIT covers both editing that list AND closing
    // a task on it, since a task is completed by editing the list it belongs to.
    INSTALLATION__VIEW_TASKLIST: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "View Tasklist",
    },
    INSTALLATION__ADD_TASKLIST: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Add Tasklist",
    },
    INSTALLATION__EDIT_TASKLIST: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.INSTALLATION,
        title: "Edit Tasklist",
    },

    // ── EV Application (module_id: 2, submodule_id: 2) ───────────────────────
    EV_APP__VIEW_TICKETS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "View Tickets",
    },
    EV_APP__VIEW_COMMENTS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "View Comments",
    },
    EV_APP__VIEW_TICKET_HISTORY: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "View Ticket History",
    },
    EV_APP__ADD_TICKETS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Add Tickets",
    },
    EV_APP__ADD_COMMENTS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Add Comments",
    },
    EV_APP__EDIT_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Edit Ticket",
    },
    EV_APP__UPDATE_TICKET_STATUS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Update ticket status",
    },
    EV_APP__REASSIGN_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Reassign ticket",
    },
    EV_APP__ADD_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.EV_APPLICATION, title: "Add Tag" },
    EV_APP__VIEW_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.EV_APPLICATION, title: "View Tag" },
    EV_APP__DELETE_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.EV_APPLICATION, title: "Delete Tag" },
    EV_APP__MAP_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.EV_APPLICATION, title: "Map Tag" },
    EV_APP__UNMAP_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.EV_APPLICATION, title: "Unmap Tag" },
    EV_APP__DROP_TICKETS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Drop tickets",
    },
    EV_APP__VIEW_DOWNLOAD: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "View Download",
    },
    EV_APP__VIEW_HISTORY: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "View History",
    },
    EV_APP__VIEW_DETAILS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "View Details",
    },
    EV_APP__EDIT_ACTION_BUTTON: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Edit Action button",
    },
    EV_APP__EDIT_EDIT_STATUS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Edit Edit Status",
    },
    EV_APP__EDIT_DROP_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Edit Drop Ticket",
    },
    EV_APP__EDIT_BLUE_CARD: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Edit Blue Card",
    },
    EV_APP__EDIT_ADD_BILL: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.EV_APPLICATION,
        title: "Edit Add Bill",
    },

    // ── Survey (module_id: 2, submodule_id: 12) ───────────────────────────────
    SURVEY__VIEW_TICKETS: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "View Tickets" },
    SURVEY__VIEW_COMMENTS: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "View Comments" },
    SURVEY__VIEW_TICKET_HISTORY: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "View Ticket History",
    },
    SURVEY__ADD_TICKETS: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Add Tickets" },
    SURVEY__ADD_COMMENTS: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Add Comments" },
    SURVEY__EDIT_TICKET: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Edit Ticket" },
    SURVEY__DROP_TICKETS: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Drop tickets" },
    SURVEY__UPDATE_TICKET_STATUS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "Update ticket status",
    },
    SURVEY__REASSIGN_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "Reassign ticket",
    },
    SURVEY__ADD_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Add Tag" },
    SURVEY__VIEW_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "View Tag" },
    SURVEY__DELETE_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Delete Tag" },
    SURVEY__MAP_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Map Tag" },
    SURVEY__UNMAP_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Unmap Tag" },
    SURVEY__VIEW_DOWNLOAD: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "View Download" },
    SURVEY__VIEW_OVERVIEW: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "View Overview" },
    SURVEY__VIEW_SURVEY_DETAIL: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "View Survey Detail",
    },
    SURVEY__VIEW_ESTIMATE_TAB: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "View Estimate Tab",
    },
    SURVEY__VIEW_DOWNLOAD_ESTIMATE_TAB: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "View Download Estimate Tab",
    },
    SURVEY__VIEW_MATERIAL_DETAILS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "View Material Details",
    },
    SURVEY__VIEW_DOWNLOAD_MATERIAL_BOQ: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "View Download Material BOQ",
    },
    SURVEY__VIEW_SALES_TAB: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "View Sales Tab" },
    SURVEY__VIEW_DOC_HUB: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "View Doc Hub" },
    SURVEY__VIEW_APPROVAL_TAB: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "View Approval Tab",
    },
    SURVEY__ADD_ON_GROUND_POC: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "Add On-Ground POC",
    },
    SURVEY__ADD_CONTRACTOR: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Add Contractor" },
    SURVEY__ADD_IMAGES: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Add Images" },
    SURVEY__ADD_ESTIMATE: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Add Estimate" },
    SURVEY__ADD_DOC: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Add Doc" },
    SURVEY__ADD_CHARGER: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Add Charger" },
    SURVEY__ADD_APPROVAL: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Add Approval" },
    SURVEY__EDIT_CHARGER: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Edit Charger" },
    SURVEY__EDIT_ESTIMATE: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Edit Estimate" },
    SURVEY__EDIT_MATERIAL_DETAILS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "Edit Material Details",
    },
    SURVEY__EDIT_CUSTOMER_POC: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "Edit Customer POC",
    },
    SURVEY__EDIT_BULK_GRN: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Edit Bulk GRN" },
    SURVEY__EDIT_RECEIVABLES_DETAILS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "Edit Receivables Details",
    },
    SURVEY__EDIT_DOC_HUB: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Edit Doc Hub" },
    SURVEY__EDIT_ACTION_BUTTON: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "Edit Action button",
    },
    SURVEY__EDIT_STATUS: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Edit Status" },
    SURVEY__EDIT_DROP_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "Edit Drop Ticket",
    },
    SURVEY__EDIT_ON_HOLD: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Edit on-hold" },
    SURVEY__EDIT_IMAGES: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.SURVEY, title: "Edit Images" },
    SURVEY__EDIT_SURVEY_DETAILS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.SURVEY,
        title: "Edit Survey Details",
    },

    // ── Demolition (module_id: 2, submodule_id: 21) ───────────────────────────
    DEMOLITION__VIEW_TICKETS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "View Tickets",
    },
    DEMOLITION__VIEW_COMMENTS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "View Comments",
    },
    DEMOLITION__VIEW_TICKET_HISTORY: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "View Ticket History",
    },
    DEMOLITION__ADD_TICKET: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "Add Ticket" },
    DEMOLITION__ADD_COMMENTS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Add Comments",
    },
    DEMOLITION__EDIT_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Edit Ticket",
    },
    DEMOLITION__UPDATE_TICKET_STATUS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Update ticket status",
    },
    DEMOLITION__REASSIGN_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Reassign ticket",
    },
    DEMOLITION__BLOCK_TICKET: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Block Ticket",
    },
    DEMOLITION__ADD_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "Add Tag" },
    DEMOLITION__VIEW_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "View Tag" },
    DEMOLITION__DELETE_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "Delete Tag" },
    DEMOLITION__EDIT_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "Edit Tag" },
    DEMOLITION__MAP_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "Map Tag" },
    DEMOLITION__UNMAP_TAG: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "Unmap Tag" },
    DEMOLITION__VIEW_DOWNLOAD: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "View Download",
    },
    DEMOLITION__VIEW_LINKED_TICKETS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "View Linked Tickets",
    },
    DEMOLITION__VIEW_MATERIAL_DETAILS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "View Material Details",
    },
    DEMOLITION__VIEW_CONTRACTOR_MGMT: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "View Contractor Management",
    },
    DEMOLITION__VIEW_SALES: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "View Sales" },
    DEMOLITION__VIEW_DOC_HUB: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "View Doc Hub",
    },
    DEMOLITION__EDIT_ADDRESSES: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Edit Addresses",
    },
    DEMOLITION__EDIT_CONNECTION_DETAILS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Edit Connection Details",
    },
    DEMOLITION__EDIT_STATUS: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Edit Status",
    },
    DEMOLITION__EDIT_CONTRACTOR_MGMT: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Edit Contractor Management",
    },
    DEMOLITION__EDIT_SALES: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "Edit Sales" },
    DEMOLITION__EDIT_DOC_HUB: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Edit Doc Hub",
    },
    DEMOLITION__ADD_LINK_SURVEY: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Add Link Survey",
    },
    DEMOLITION__ADD_MAP_ACCOUNT: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Add Map Account",
    },
    DEMOLITION__ADD_CHANGE_ORDER: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Add Change Order",
    },
    DEMOLITION__ADD_CONTRACTOR_MGMT: {
        module: EModuleIds.OPERATIONS,
        submodule: ESubModuleIds.DEMOLITION,
        title: "Add Contractor Management",
    },
    DEMOLITION__ADD_SALES: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "Add Sales" },
    DEMOLITION__ADD_DOC: { module: EModuleIds.OPERATIONS, submodule: ESubModuleIds.DEMOLITION, title: "Add Doc" },

    // ── Logistics (module_id: 3) ─────────────────────────────────────────────
    LOGISTICS__VIEW_TICKETS: { module: EModuleIds.LOGISTICS, title: "View Tickets" },
    LOGISTICS__VIEW_COMMENTS: { module: EModuleIds.LOGISTICS, title: "View Comments" },
    LOGISTICS__VIEW_TICKET_HISTORY: { module: EModuleIds.LOGISTICS, title: "View Ticket History" },
    LOGISTICS__ADD_COMMENTS: { module: EModuleIds.LOGISTICS, title: "Add Comments" },
    LOGISTICS__EDIT_TICKET_DETAILS: { module: EModuleIds.LOGISTICS, title: "Edit Ticket Details" },
    LOGISTICS__UPDATE_TICKET_STATUS: { module: EModuleIds.LOGISTICS, title: "Update ticket status" },
    LOGISTICS__REASSIGN_TICKET: { module: EModuleIds.LOGISTICS, title: "Reassign ticket" },
    LOGISTICS__EDIT_DC_DETAILS: { module: EModuleIds.LOGISTICS, title: "Edit DC Details" },
    LOGISTICS__EDIT_POST_DELIVERY_IMGS: { module: EModuleIds.LOGISTICS, title: "Edit Post Delivery Images" },
    LOGISTICS__DELETE_POST_DELIVERY_IMGS: { module: EModuleIds.LOGISTICS, title: "Delete Post Delivery Images" },
    LOGISTICS__BULK_UPDATE_COST: { module: EModuleIds.LOGISTICS, title: "Bulk Update Logistic Cost" },
    LOGISTICS__ADD_TAG: { module: EModuleIds.LOGISTICS, title: "Add Tag" },
    LOGISTICS__VIEW_TAG: { module: EModuleIds.LOGISTICS, title: "View Tag" },
    LOGISTICS__DELETE_TAG: { module: EModuleIds.LOGISTICS, title: "Delete Tag" },
    LOGISTICS__MAP_TAG: { module: EModuleIds.LOGISTICS, title: "Map Tag" },
    LOGISTICS__UNMAP_TAG: { module: EModuleIds.LOGISTICS, title: "Unmap Tag" },
    LOGISTICS__ADD_TICKETS: { module: EModuleIds.LOGISTICS, title: "Add Tickets" },
    LOGISTICS__VIEW_DOWNLOAD: { module: EModuleIds.LOGISTICS, title: "View Download" },
    LOGISTICS__VIEW_HISTORY: { module: EModuleIds.LOGISTICS, title: "View History" },
    LOGISTICS__EDIT_DELIVERY_DATE: { module: EModuleIds.LOGISTICS, title: "Edit Delivery Date" },
    LOGISTICS__EDIT_EXECUTED_BY: { module: EModuleIds.LOGISTICS, title: "Edit Executed By" },
    LOGISTICS__EDIT_COMMENTS: { module: EModuleIds.LOGISTICS, title: "Edit Comments" },
    LOGISTICS__EDIT_ON_HOLD: { module: EModuleIds.LOGISTICS, title: "Edit on-hold" },
    LOGISTICS__EDIT_BULK_UPDATE: { module: EModuleIds.LOGISTICS, title: "Edit Bulk Update" },
    LOGISTICS__EDIT_SOURCE_ADDRESS: { module: EModuleIds.LOGISTICS, title: "Edit Source Address" },
    LOGISTICS__EDIT_DESTINATION_ADDRESS: { module: EModuleIds.LOGISTICS, title: "Edit Destination Address" },
    LOGISTICS__EDIT_DESTINATION_POC: { module: EModuleIds.LOGISTICS, title: "Edit Destination POC" },
    LOGISTICS__EDIT_SOURCE_POC: { module: EModuleIds.LOGISTICS, title: "Edit Source POC" },
    LOGISTICS__EDIT_DELIVERY_POC: { module: EModuleIds.LOGISTICS, title: "Edit Delivery POC" },
    LOGISTICS__EDIT_SHIPMENT_DETAILS: { module: EModuleIds.LOGISTICS, title: "Edit Shipment Details" },
    LOGISTICS__EDIT_TICKET_STATUS: { module: EModuleIds.LOGISTICS, title: "Edit Ticket Status" },
    LOGISTICS__EDIT_DROP_TICKET: { module: EModuleIds.LOGISTICS, title: "Edit Drop Ticket" },
    LOGISTICS__ADD_POST_DELIVERY_IMGS: { module: EModuleIds.LOGISTICS, title: "Add Post Delivery images" },
    LOGISTICS__ADD_SHIPMENT_DETAILS: { module: EModuleIds.LOGISTICS, title: "Add Shipment Details" },
    LOGISTICS__VIEW_LINKED_TICKETS: { module: EModuleIds.LOGISTICS, title: "View Linked Tickets" },

    // ── Admin (module_id: 5) ─────────────────────────────────────────────────
    ADMIN__VIEW_USER_LIST: { module: EModuleIds.ADMIN, title: "View User list" },
    ADMIN__ADD_USER: { module: EModuleIds.ADMIN, title: "Add User" },
    ADMIN__EDIT_USER: { module: EModuleIds.ADMIN, title: "Edit User" },
    ADMIN__ACTIVATE_DEACTIVATE_USER: { module: EModuleIds.ADMIN, title: "Activate/Deactivate user" },
    ADMIN__ADD_ROLE: { module: EModuleIds.ADMIN, title: "Add Role" },
    ADMIN__EDIT_ROLE: { module: EModuleIds.ADMIN, title: "Edit Role" },
    ADMIN__EDIT_TEAM_POSITION: { module: EModuleIds.ADMIN, title: "Edit Team Position" },
    ADMIN__VIEW_ALL_ROLES: { module: EModuleIds.ADMIN, title: "View All Roles" },
    ADMIN__VIEW_TEAM_LIST: { module: EModuleIds.ADMIN, title: "View Team List" },
    ADMIN__ADD_TEAM: { module: EModuleIds.ADMIN, title: "Add Team" },
    ADMIN__EDIT_TEAM: { module: EModuleIds.ADMIN, title: "Edit Team" },
    ADMIN__ADD_POSITION: { module: EModuleIds.ADMIN, title: "Add Position" },
    ADMIN__ADD_TAG: { module: EModuleIds.ADMIN, title: "Add Tag" },
    ADMIN__VIEW_TAG: { module: EModuleIds.ADMIN, title: "View Tag" },
    ADMIN__DELETE_TAG: { module: EModuleIds.ADMIN, title: "Delete Tag" },
    ADMIN__EDIT_TAG: { module: EModuleIds.ADMIN, title: "Edit Tag" },

    // ── Settings (module_id: 6) ──────────────────────────────────────────────
    SETTINGS__VIEW_REASONS: { module: EModuleIds.SETTINGS, title: "View Reasons" },
    SETTINGS__VIEW_FORMAT: { module: EModuleIds.SETTINGS, title: "View Format" },
    SETTINGS__VIEW_RULES: { module: EModuleIds.SETTINGS, title: "View Rules" },
    SETTINGS__VIEW_PROFILE_DETAILS: { module: EModuleIds.SETTINGS, title: "View Profile Details" },
    SETTINGS__ADD_REASON: { module: EModuleIds.SETTINGS, title: "Add Reason" },
    SETTINGS__ADD_FORMAT: { module: EModuleIds.SETTINGS, title: "Add Format" },
    SETTINGS__ADD_RULES: { module: EModuleIds.SETTINGS, title: "Add Rules" },
    SETTINGS__EDIT_REASONS: { module: EModuleIds.SETTINGS, title: "Edit Reasons" },
    SETTINGS__UPDATE_FORMAT: { module: EModuleIds.SETTINGS, title: "Update Format" },
    SETTINGS__UPDATE_RULE: { module: EModuleIds.SETTINGS, title: "Update Rule" },
    SETTINGS__UPDATE_PROFILE_DETAILS: { module: EModuleIds.SETTINGS, title: "Update Profile Details" },
    SETTINGS__DELETE_REASONS: { module: EModuleIds.SETTINGS, title: "Delete Reasons" },
    SETTINGS__DELETE_FORMAT: { module: EModuleIds.SETTINGS, title: "Delete Format" },
    SETTINGS__DELETE_RULE: { module: EModuleIds.SETTINGS, title: "Delete Rule" },
    SETTINGS__ACTIVATE_DEACTIVATE_REASONS: { module: EModuleIds.SETTINGS, title: "Activate/Deactivate Reasons" },
    SETTINGS__VIEW_PROJECT_TEMPLATES: { module: EModuleIds.SETTINGS, title: "View Project Templates" },
    SETTINGS__EDIT_PROJECT_TEMPLATES: { module: EModuleIds.SETTINGS, title: "Edit Project Templates" },
    SETTINGS__ADD_PROJECT_TEMPLATES: { module: EModuleIds.SETTINGS, title: "Add Project Templates" },
    SETTINGS__VIEW_ITEM_MASTER: { module: EModuleIds.SETTINGS, title: "View Item Master" },
    SETTINGS__ADD_ITEM_MASTER: { module: EModuleIds.SETTINGS, title: "Add Item Master" },
    SETTINGS__EDIT_ITEM_MASTER: { module: EModuleIds.SETTINGS, title: "Edit Item Master" },
    SETTINGS__TOGGLE_ITEM_MASTER: { module: EModuleIds.SETTINGS, title: "Toggle Item Master" },
    SETTINGS__VIEW_ACCOUNT_RATE_CARD: { module: EModuleIds.SETTINGS, title: "View Account Rate Card" },
    SETTINGS__ADD_ACCOUNT_RATE_CARD: { module: EModuleIds.SETTINGS, title: "Add Account Rate Card" },
    SETTINGS__EDIT_ACCOUNT_RATE_CARD: { module: EModuleIds.SETTINGS, title: "Edit Account Rate Card" },
    SETTINGS__DELETE_ACCOUNT_RATE_CARD: { module: EModuleIds.SETTINGS, title: "Delete Account Rate Card" },
    SETTINGS__VIEW_BOQ: { module: EModuleIds.SETTINGS, title: "View Boq" },
    SETTINGS__ADD_BOQ: { module: EModuleIds.SETTINGS, title: "Add Boq" },
    SETTINGS__EDIT_BOQ: { module: EModuleIds.SETTINGS, title: "Edit Boq" },
    SETTINGS__DELETE_BOQ: { module: EModuleIds.SETTINGS, title: "Delete Boq" },
    SETTINGS__VIEW_NOTIFICATION: { module: EModuleIds.SETTINGS, title: "View Notification" },
    SETTINGS__ADD_NOTIFICATION: { module: EModuleIds.SETTINGS, title: "Add Notification" },
    SETTINGS__EDIT_NOTIFICATION: { module: EModuleIds.SETTINGS, title: "Edit Notification" },
    SETTINGS__VIEW_FEEDBACK_FORMS: { module: EModuleIds.SETTINGS, title: "View Feedback Forms" },
    SETTINGS__ADD_FEEDBACK_FORMS: { module: EModuleIds.SETTINGS, title: "Add Feedback Forms" },
    SETTINGS__EDIT_FEEDBACK_FORMS: { module: EModuleIds.SETTINGS, title: "Edit Feedback Forms" },
    SETTINGS__DELETE_FEEDBACK_FORMS: { module: EModuleIds.SETTINGS, title: "Delete Feedback Forms" },

    // ── Finance (module_id: 8) ───────────────────────────────────────────────
    FINANCE__VIEW_TICKETS: { module: EModuleIds.FINANCE, title: "View Tickets" },
    FINANCE__VIEW_COMMENTS: { module: EModuleIds.FINANCE, title: "View Comments" },
    FINANCE__VIEW_TICKET_HISTORY: { module: EModuleIds.FINANCE, title: "View Ticket History" },
    FINANCE__ADD_COMMENTS: { module: EModuleIds.FINANCE, title: "Add Comments" },
    FINANCE__EDIT_TICKET: { module: EModuleIds.FINANCE, title: "Edit Ticket" },
    FINANCE__UPDATE_TICKET_STATUS: { module: EModuleIds.FINANCE, title: "Update ticket status" },
    FINANCE__REASSIGN_TICKET: { module: EModuleIds.FINANCE, title: "Reassign ticket" },
    FINANCE__BLOCK_TICKET: { module: EModuleIds.FINANCE, title: "Block Ticket" },
    FINANCE__EDIT_CHARGER_DETAILS: { module: EModuleIds.FINANCE, title: "Edit Charger Details" },
    FINANCE__ADD_TAG: { module: EModuleIds.FINANCE, title: "Add Tag" },
    FINANCE__VIEW_TAG: { module: EModuleIds.FINANCE, title: "View Tag" },
    FINANCE__DELETE_TAG: { module: EModuleIds.FINANCE, title: "Delete Tag" },
    FINANCE__MAP_TAG: { module: EModuleIds.FINANCE, title: "Map Tag" },
    FINANCE__UNMAP_TAG: { module: EModuleIds.FINANCE, title: "Unmap Tag" },
    FINANCE__VIEW_DOWNLOAD: { module: EModuleIds.FINANCE, title: "View Download" },
    FINANCE__VIEW_DOC_HUB: { module: EModuleIds.FINANCE, title: "View Doc Hub" },
    FINANCE__VIEW_HISTORY: { module: EModuleIds.FINANCE, title: "View History" },
    FINANCE__VIEW_OVERVIEW: { module: EModuleIds.FINANCE, title: "View Overview" },
    FINANCE__VIEW_SALES: { module: EModuleIds.FINANCE, title: "View Sales" },
    FINANCE__VIEW_MATERIALS: { module: EModuleIds.FINANCE, title: "View Materials" },
    FINANCE__EDIT_APPROVE_CIN: { module: EModuleIds.FINANCE, title: "Edit Approve CIN" },
    FINANCE__EDIT_APPROVE_TAX_INVOICE: { module: EModuleIds.FINANCE, title: "Edit Approve Tax Invoice" },
    FINANCE__EDIT_VERIFY_PAYMENT: { module: EModuleIds.FINANCE, title: "Edit Verify Payment" },
    FINANCE__EDIT_PAYMENT_DETAILS: { module: EModuleIds.FINANCE, title: "Edit Payment Details" },
    FINANCE__EDIT_BULK_ACTIONS: { module: EModuleIds.FINANCE, title: "Edit Bulk Actions" },
    FINANCE__ADD_DOCUMENTS: { module: EModuleIds.FINANCE, title: "Add Documents" },
    FINANCE__ADD_INVOICE: { module: EModuleIds.FINANCE, title: "Add Invoice" },
    FINANCE__VIEW_LINKED_TICKETS: { module: EModuleIds.FINANCE, title: "View Linked Tickets" },

    // ── Onboarding (module_id: 9) ────────────────────────────────────────────
    ONBOARDING__VIEW_TICKETS: { module: EModuleIds.ONBOARDING, title: "View Tickets" },
    ONBOARDING__VIEW_TAG: { module: EModuleIds.ONBOARDING, title: "View Tag" },
    ONBOARDING__VIEW_DOWNLOAD: { module: EModuleIds.ONBOARDING, title: "View Download" },
    ONBOARDING__VIEW_HISTORY: { module: EModuleIds.ONBOARDING, title: "View History" },
    ONBOARDING__VIEW_DETAILS: { module: EModuleIds.ONBOARDING, title: "View Details" },
    ONBOARDING__VIEW_CHARGER_DETAILS: { module: EModuleIds.ONBOARDING, title: "View Charger Details" },
    ONBOARDING__VIEW_CPO_DETAILS: { module: EModuleIds.ONBOARDING, title: "View CPO Details" },
    ONBOARDING__VIEW_BANK_DETAILS: { module: EModuleIds.ONBOARDING, title: "View Bank Details" },
    ONBOARDING__VIEW_CPO_REVENUE_DETAILS: { module: EModuleIds.ONBOARDING, title: "View CPO revenue Details" },
    ONBOARDING__VIEW_HOST_DETAILS: { module: EModuleIds.ONBOARDING, title: "View Host Details" },
    ONBOARDING__VIEW_TICKET_DETAILS: { module: EModuleIds.ONBOARDING, title: "View Ticket Details" },
    ONBOARDING__EDIT_EXECUTED_BY: { module: EModuleIds.ONBOARDING, title: "Edit Executed by" },
    ONBOARDING__EDIT_STATION_DETAIL: { module: EModuleIds.ONBOARDING, title: "Edit Station detail" },
    ONBOARDING__EDIT_CHARGER_DETAIL: { module: EModuleIds.ONBOARDING, title: "Edit Charger detail" },
    ONBOARDING__EDIT_BILLING_ADDRESS: { module: EModuleIds.ONBOARDING, title: "Edit Billing Address" },
    ONBOARDING__EDIT_BANK_DETAILS: { module: EModuleIds.ONBOARDING, title: "Edit Bank Details" },
    ONBOARDING__EDIT_CPO_REVENUE_DETAILS: { module: EModuleIds.ONBOARDING, title: "Edit CPO Revenue Details" },
    ONBOARDING__EDIT_HOST_DETAILS: { module: EModuleIds.ONBOARDING, title: "Edit Host Details" },
    ONBOARDING__EDIT_CHARGER_DEAL: { module: EModuleIds.ONBOARDING, title: "Edit Charger Deal" },
    ONBOARDING__EDIT_ON_HOLD: { module: EModuleIds.ONBOARDING, title: "Edit on-hold" },
    ONBOARDING__EDIT_STATUS: { module: EModuleIds.ONBOARDING, title: "Edit Status" },
    ONBOARDING__EDIT_DROP: { module: EModuleIds.ONBOARDING, title: "Edit Drop" },
    ONBOARDING__EDIT_PUSH_TO_CSMS: { module: EModuleIds.ONBOARDING, title: "Edit Push To csms" },
    ONBOARDING__ADD_TAG: { module: EModuleIds.ONBOARDING, title: "Add Tag" },
    ONBOARDING__MAP_TAG: { module: EModuleIds.ONBOARDING, title: "Map Tag" },
    ONBOARDING__UNMAP_TAG: { module: EModuleIds.ONBOARDING, title: "Unmap Tag" },
    ONBOARDING__ADD_COMMENTS: { module: EModuleIds.ONBOARDING, title: "Add Comments" },
    ONBOARDING__VIEW_COMMENTS: { module: EModuleIds.ONBOARDING, title: "View Comments" },
    ONBOARDING__EDIT_COMMENTS: { module: EModuleIds.ONBOARDING, title: "Edit Comments" },

    // ── Tech Support (module_id: 10) ─────────────────────────────────────────
    TECH_SUPPORT__VIEW_TICKETS: { module: EModuleIds.TECHSUPPORT, title: "View Tickets" },
    TECH_SUPPORT__VIEW_COMMENTS: { module: EModuleIds.TECHSUPPORT, title: "View Comments" },
    TECH_SUPPORT__VIEW_TICKET_HISTORY: { module: EModuleIds.TECHSUPPORT, title: "View Ticket History" },
    TECH_SUPPORT__ADD_COMMENTS: { module: EModuleIds.TECHSUPPORT, title: "Add Comments" },
    TECH_SUPPORT__EDIT_TICKET: { module: EModuleIds.TECHSUPPORT, title: "Edit Ticket" },
    TECH_SUPPORT__UPDATE_TICKET_STATUS: { module: EModuleIds.TECHSUPPORT, title: "Update ticket status" },
    TECH_SUPPORT__REASSIGN_TICKET: { module: EModuleIds.TECHSUPPORT, title: "Reassign ticket" },
    TECH_SUPPORT__BLOCK_TICKET: { module: EModuleIds.TECHSUPPORT, title: "Block Ticket" },
    TECH_SUPPORT__EDIT_CHARGER_DETAILS: { module: EModuleIds.TECHSUPPORT, title: "Edit Charger Details" },
    TECH_SUPPORT__ADD_TAG: { module: EModuleIds.TECHSUPPORT, title: "Add Tag" },
    TECH_SUPPORT__VIEW_TAG: { module: EModuleIds.TECHSUPPORT, title: "View Tag" },
    TECH_SUPPORT__MAP_TAG: { module: EModuleIds.TECHSUPPORT, title: "Map Tag" },
    TECH_SUPPORT__UNMAP_TAG: { module: EModuleIds.TECHSUPPORT, title: "Unmap Tag" },

    // ── Customer Support (module_id: 11) ─────────────────────────────────────
    CUSTOMER_SUPPORT__VIEW_TICKETS: { module: EModuleIds.CUSTOMER_SUPPORT, title: "View Tickets" },
    CUSTOMER_SUPPORT__VIEW_COMMENTS: { module: EModuleIds.CUSTOMER_SUPPORT, title: "View Comments" },
    CUSTOMER_SUPPORT__VIEW_TICKET_HISTORY: { module: EModuleIds.CUSTOMER_SUPPORT, title: "View Ticket History" },
    CUSTOMER_SUPPORT__ADD_COMMENTS: { module: EModuleIds.CUSTOMER_SUPPORT, title: "Add Comments" },
    CUSTOMER_SUPPORT__EDIT_TICKET: { module: EModuleIds.CUSTOMER_SUPPORT, title: "Edit Ticket" },
    CUSTOMER_SUPPORT__UPDATE_TICKET_STATUS: { module: EModuleIds.CUSTOMER_SUPPORT, title: "Update ticket status" },
    CUSTOMER_SUPPORT__REASSIGN_TICKET: { module: EModuleIds.CUSTOMER_SUPPORT, title: "Reassign ticket" },
    CUSTOMER_SUPPORT__BLOCK_TICKET: { module: EModuleIds.CUSTOMER_SUPPORT, title: "Block Ticket" },
    CUSTOMER_SUPPORT__ADD_TAG: { module: EModuleIds.CUSTOMER_SUPPORT, title: "Add Tag" },
    CUSTOMER_SUPPORT__VIEW_TAG: { module: EModuleIds.CUSTOMER_SUPPORT, title: "View Tag" },
    CUSTOMER_SUPPORT__MAP_TAG: { module: EModuleIds.CUSTOMER_SUPPORT, title: "Map Tag" },
    CUSTOMER_SUPPORT__UNMAP_TAG: { module: EModuleIds.CUSTOMER_SUPPORT, title: "Unmap Tag" },

    // ── Network Reliability (module_id: 13) ──────────────────────────────────
    NETWORK_RELIABILITY__VIEW_TICKETS: { module: EModuleIds.NETWORK_RELIABILITY, title: "View Tickets" },
    NETWORK_RELIABILITY__VIEW_COMMENTS: { module: EModuleIds.NETWORK_RELIABILITY, title: "View Comments" },
    NETWORK_RELIABILITY__VIEW_TICKET_HISTORY: { module: EModuleIds.NETWORK_RELIABILITY, title: "View Ticket History" },
    NETWORK_RELIABILITY__ADD_COMMENTS: { module: EModuleIds.NETWORK_RELIABILITY, title: "Add Comments" },
    NETWORK_RELIABILITY__EDIT_TICKET: { module: EModuleIds.NETWORK_RELIABILITY, title: "Edit Ticket" },
    NETWORK_RELIABILITY__UPDATE_TICKET_STATUS: {
        module: EModuleIds.NETWORK_RELIABILITY,
        title: "Update ticket status",
    },
    NETWORK_RELIABILITY__REASSIGN_TICKET: { module: EModuleIds.NETWORK_RELIABILITY, title: "Reassign ticket" },

    // ── Accounts (module_id: 14) ─────────────────────────────────────────────
    ACCOUNTS__LIST_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "List Accounts" },
    ACCOUNTS__ADD_ACCOUNT: { module: EModuleIds.ACCOUNTS, title: "Add Account" },
    ACCOUNTS__ADD_ACCOUNT_FROM_PROJECTS: { module: EModuleIds.ACCOUNTS, title: "Add Account From Projects" },
    ACCOUNTS__VIEW_ALL_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "View All Accounts" },
    ACCOUNTS__VIEW_CLIENT_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "View Client Accounts" },
    ACCOUNTS__VIEW_VENDOR_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "View Vendor Accounts" },
    ACCOUNTS__EDIT_ACCOUNT: { module: EModuleIds.ACCOUNTS, title: "Edit Account" },
    ACCOUNTS__EXPORT: { module: EModuleIds.ACCOUNTS, title: "Export" },
    ACCOUNTS__ADD_TAG: { module: EModuleIds.ACCOUNTS, title: "Add Tag" },
    ACCOUNTS__VIEW_TAG: { module: EModuleIds.ACCOUNTS, title: "View Tag" },
    ACCOUNTS__DELETE_TAG: { module: EModuleIds.ACCOUNTS, title: "Delete Tag" },
    ACCOUNTS__MAP_TAG: { module: EModuleIds.ACCOUNTS, title: "Map Tag" },
    ACCOUNTS__UNMAP_TAG: { module: EModuleIds.ACCOUNTS, title: "Unmap Tag" },
    ACCOUNTS__ADD_ALL_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "Add All Accounts" },
    ACCOUNTS__ADD_CLIENT_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "Add Client Accounts" },
    ACCOUNTS__ADD_VENDOR_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "Add Vendor Accounts" },
    ACCOUNTS__EDIT_ALL_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "Edit All Accounts" },
    ACCOUNTS__EDIT_CLIENT_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "Edit Client Accounts" },
    ACCOUNTS__EDIT_VENDOR_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "Edit Vendor Accounts" },
    ACCOUNTS__DELETE_ALL_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "Delete All Accounts" },
    ACCOUNTS__DELETE_CLIENT_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "Delete Client Accounts" },
    ACCOUNTS__DELETE_VENDOR_ACCOUNTS: { module: EModuleIds.ACCOUNTS, title: "Delete Vendor Accounts" },

    // ── Sites (module_id: 15) ────────────────────────────────────────────────
    SITES__ADD_TICKET: { module: EModuleIds.SITES, title: "Add Ticket" },
    SITES__VIEW_TICKETS: { module: EModuleIds.SITES, title: "View Tickets" },
    // The Sites top tabs (Project Sites / Statiq Warehouses); resolved with a fallback in pages/Sites/useSiteTabAccess.
    SITES__VIEW_SITES: { module: EModuleIds.SITES, title: "View Sites" },
    SITES__VIEW_WAREHOUSE: { module: EModuleIds.SITES, title: "View Warehouse" },
    SITES__EDIT_TICKET: { module: EModuleIds.SITES, title: "Edit Ticket" },
    SITES__ADD_TAG: { module: EModuleIds.SITES, title: "Add Tag" },
    SITES__VIEW_TAG: { module: EModuleIds.SITES, title: "View Tag" },
    SITES__DELETE_TAG: { module: EModuleIds.SITES, title: "Delete Tag" },
    SITES__MAP_TAG: { module: EModuleIds.SITES, title: "Map Tag" },
    SITES__UNMAP_TAG: { module: EModuleIds.SITES, title: "Unmap Tag" },
    SITES__DOWNLOAD_TICKETS: { module: EModuleIds.SITES, title: "Download Tickets" },
    SITES__VIEW_TICKET_DETAIL: { module: EModuleIds.SITES, title: "View Ticket Detail" },
    SITES__VIEW_STAKEHOLDER_TAB: { module: EModuleIds.SITES, title: "View Stakeholder Tab" },
    SITES__VIEW_PROJECT_TAB: { module: EModuleIds.SITES, title: "View Project Tab" },
    SITES__VIEW_TICKETS_TAB: { module: EModuleIds.SITES, title: "View Tickets Tab" },
    SITES__VIEW_DOCUMENT_TAB: { module: EModuleIds.SITES, title: "View Document Tab" },
    SITES__VIEW_CONNECTION_TAB: { module: EModuleIds.SITES, title: "View Connection Tab" },
    SITES__VIEW_ASSETS_TAB: { module: EModuleIds.SITES, title: "View Assets Tab" },
    SITES__VIEW_OTHER_DETAILS: { module: EModuleIds.SITES, title: "View Other Details" },
    SITES__VIEW_CHARGERS_TAB: { module: EModuleIds.SITES, title: "View Chargers Tab" },
    SITES__VIEW_MATERIALS_TAB: { module: EModuleIds.SITES, title: "View Materials Tab" },
    SITES__VIEW_VENDOR_BILLS: { module: EModuleIds.SITES, title: "View Vendor Bills" },
    SITES__VIEW_CONNECTION_EXPENSE: { module: EModuleIds.SITES, title: "View Connection Expense" },
    SITES__VIEW_HISTORY: { module: EModuleIds.SITES, title: "View History" },
    SITES__EDIT_CAPITALISATION_STATUS: { module: EModuleIds.SITES, title: "Edit Capitalistion Status" },
    SITES__EDIT_ADDRESS: { module: EModuleIds.SITES, title: "Edit Address" },
    SITES__EDIT_SITE_IMAGES: { module: EModuleIds.SITES, title: "Edit Site Images" },
    SITES__ADD_SITE_IMAGES: { module: EModuleIds.SITES, title: "Add Site Images" },
    SITES__ADD_PROJECT: { module: EModuleIds.SITES, title: "Add Project" },
    SITES__ADD_DOCUMENTS: { module: EModuleIds.SITES, title: "Add Documents" },

    // ── Tags (module_id: 17) ─────────────────────────────────────────────────
    TAGS__ADD_TAG: { module: EModuleIds.TAGS, title: "Add Tag" },
    TAGS__EDIT_TAG: { module: EModuleIds.TAGS, title: "Edit Tag" },
    TAGS__VIEW_TAG: { module: EModuleIds.TAGS, title: "View Tag" },
    TAGS__DELETE_TAG: { module: EModuleIds.TAGS, title: "Delete Tag" },

    // ── Programs (module_id: 18) ─────────────────────────────────────────────
    PROGRAMS__VIEW_TICKETS: { module: EModuleIds.PROGRAMS, title: "View Tickets" },
    PROGRAMS__ADD_TICKET: { module: EModuleIds.PROGRAMS, title: "Add Ticket" },
    PROGRAMS__EDIT_TICKET: { module: EModuleIds.PROGRAMS, title: "Edit Ticket" },
    PROGRAMS__DELETE_TICKET: { module: EModuleIds.PROGRAMS, title: "Delete Ticket" },
    PROGRAMS__UPDATE_TICKET_STATUS: { module: EModuleIds.PROGRAMS, title: "Update Ticket status" },
    PROGRAMS__MAP_TAG: { module: EModuleIds.PROGRAMS, title: "Map Tag" },
    PROGRAMS__UNMAP_TAG: { module: EModuleIds.PROGRAMS, title: "Unmap Tag" },
    PROGRAMS__VIEW_TAG: { module: EModuleIds.PROGRAMS, title: "View Tag" },
    PROGRAMS__DELETE_TAG: { module: EModuleIds.PROGRAMS, title: "Delete Tag" },
    PROGRAMS__ADD_TAG: { module: EModuleIds.PROGRAMS, title: "Add Tag" },
    PROGRAMS__VIEW_DOWNLOAD: { module: EModuleIds.PROGRAMS, title: "View Download" },
    PROGRAMS__VIEW_DETAIL_PAGE: { module: EModuleIds.PROGRAMS, title: "View Detail Page" },
    PROGRAMS__VIEW_PROJECT_TAB: { module: EModuleIds.PROGRAMS, title: "View Project Tab" },
    PROGRAMS__VIEW_SITE_TAB: { module: EModuleIds.PROGRAMS, title: "View Site Tab" },
    PROGRAMS__VIEW_BUDGET_TAB: { module: EModuleIds.PROGRAMS, title: "View Budget Tab" },
    PROGRAMS__VIEW_REPORTS_TAB: { module: EModuleIds.PROGRAMS, title: "View Reports Tab" },
    PROGRAMS__EDIT_DROP_STATUS: { module: EModuleIds.PROGRAMS, title: "Edit Drop Status" },
    PROGRAMS__EDIT_OVERVIEW: { module: EModuleIds.PROGRAMS, title: "Edit Overview" },
    PROGRAMS__EDIT_PROJECT: { module: EModuleIds.PROGRAMS, title: "Edit Project" },
    PROGRAMS__EDIT_BUDGET: { module: EModuleIds.PROGRAMS, title: "Edit Budget" },
    PROGRAMS__ADD_BUDGET: { module: EModuleIds.PROGRAMS, title: "Add Budget" },

    // ── Complaints (module_id: 19) ───────────────────────────────────────────
    COMPLAINTS__VIEW_TICKETS: { module: EModuleIds.COMPLAINTS, title: "View Tickets" },
    COMPLAINTS__ADD_TICKETS: { module: EModuleIds.COMPLAINTS, title: "Add Tickets" },
    COMPLAINTS__EDIT_TICKET: { module: EModuleIds.COMPLAINTS, title: "Edit Ticket" },
    COMPLAINTS__ADD_COMMENTS: { module: EModuleIds.COMPLAINTS, title: "Add Comments" },
    COMPLAINTS__VIEW_COMMENTS: { module: EModuleIds.COMPLAINTS, title: "View Comments" },
    COMPLAINTS__DROP_TICKET: { module: EModuleIds.COMPLAINTS, title: "Drop Ticket" },
    COMPLAINTS__VIEW_ALL_TABS: { module: EModuleIds.COMPLAINTS, title: "View All Tabs" },
    COMPLAINTS__VIEW_CPO_TAB: { module: EModuleIds.COMPLAINTS, title: "View CPO Tab" },
    COMPLAINTS__VIEW_OUTRIGHT_TAB: { module: EModuleIds.COMPLAINTS, title: "View Outright Tab" },
    COMPLAINTS__VIEW_TAGS: { module: EModuleIds.COMPLAINTS, title: "View Tags" },
    COMPLAINTS__VIEW_SITE_VISIT: { module: EModuleIds.COMPLAINTS, title: "View Site Visit" },
    COMPLAINTS__VIEW_PART_REPLACEMENT: { module: EModuleIds.COMPLAINTS, title: "View Part Replacement" },
    COMPLAINTS__VIEW_LOGISTICS: { module: EModuleIds.COMPLAINTS, title: "View Logistics" },
    COMPLAINTS__VIEW_COMPLAINTS_DOWNLOAD: { module: EModuleIds.COMPLAINTS, title: "View Complaints Download" },
    COMPLAINTS__VIEW_PART_REPLACEMENT_DL: { module: EModuleIds.COMPLAINTS, title: "View Part Replacement Download" },
    COMPLAINTS__ADD_NEW_COMPLAINT: { module: EModuleIds.COMPLAINTS, title: "Add New Complaint" },
    COMPLAINTS__ADD_SITE_VISIT: { module: EModuleIds.COMPLAINTS, title: "Add Site Visit" },
    COMPLAINTS__ADD_SITE_EXPENSE: { module: EModuleIds.COMPLAINTS, title: "Add Site Expense" },
    COMPLAINTS__ADD_PART_REPLACEMENT: { module: EModuleIds.COMPLAINTS, title: "Add Part Replacement" },
    COMPLAINTS__ADD_LOGISTICS: { module: EModuleIds.COMPLAINTS, title: "Add Logistics" },
    COMPLAINTS__ADD_TAGS: { module: EModuleIds.COMPLAINTS, title: "Add Tags" },
    COMPLAINTS__MAP_TAGS: { module: EModuleIds.COMPLAINTS, title: "Map Tags" },
    COMPLAINTS__EDIT_STATUS: { module: EModuleIds.COMPLAINTS, title: "Edit Status" },
    COMPLAINTS__EDIT_ASSIGNED_DEPARTMENT: { module: EModuleIds.COMPLAINTS, title: "Edit Assigned Department" },
    COMPLAINTS__EDIT_ASSIGNED_STATIQ_ENG: { module: EModuleIds.COMPLAINTS, title: "Edit Assigned Statiq Engineer" },
    COMPLAINTS__EDIT_ASSIGNED_ASP: { module: EModuleIds.COMPLAINTS, title: "Edit Assigned ASP" },
    COMPLAINTS__EDIT_ASSIGNED_OEM: { module: EModuleIds.COMPLAINTS, title: "Edit Assigned OEM" },
    COMPLAINTS__EDIT_PART_REPLACEMENT: { module: EModuleIds.COMPLAINTS, title: "Edit Part Replacement Details" },
    COMPLAINTS__EDIT_SITE_VISIT: { module: EModuleIds.COMPLAINTS, title: "Edit Site Visit" },
    COMPLAINTS__EDIT_LOGISTICS: { module: EModuleIds.COMPLAINTS, title: "Edit Logistics" },
    COMPLAINTS__EDIT_SITE_EXPENSE: { module: EModuleIds.COMPLAINTS, title: "Edit Site Expense" },
    COMPLAINTS__EDIT_PLANNED_DATE: { module: EModuleIds.COMPLAINTS, title: "Edit Planned Date" },
    COMPLAINTS__EDIT_COMMENTS: { module: EModuleIds.COMPLAINTS, title: "Edit Comments" },
    COMPLAINTS__EDIT_UPDATED_COMPLAINT_TYPE: { module: EModuleIds.COMPLAINTS, title: "Edit Updated Complaint Type" },
    COMPLAINTS__EDIT_CALL_TYPE: { module: EModuleIds.COMPLAINTS, title: "Edit Call Type" },
    COMPLAINTS__EDIT_ASSIGNED_ASP_ENGINEER: { module: EModuleIds.COMPLAINTS, title: "Edit Assigned ASP Engineer" },
    COMPLAINTS__REASSIGN_TO_CSMS: { module: EModuleIds.COMPLAINTS, title: "Re-assign to CSMS." },
    COMPLAINTS__VIEW_DOWNLOAD: { module: EModuleIds.COMPLAINTS, title: "View Download" },
    COMPLAINTS__EDIT_COMPLAINT_FORM: { module: EModuleIds.COMPLAINTS, title: "Edit Complaint Form" },
    COMPLAINTS__EDIT_ON_HOLD: { module: EModuleIds.COMPLAINTS, title: "Edit on-hold" },
    COMPLAINTS__ACCEPT_ASSIGNMENT: { module: EModuleIds.COMPLAINTS, title: "Accept Assignment" },
    COMPLAINTS__VIEW_ENGINEER_AVAILABILITY: {
        module: EModuleIds.COMPLAINTS,
        title: "View Engineer Availability",
    },

    // ── Approvals (module_id: 20) ────────────────────────────────────────────
    APPROVALS__VIEW_TICKETS: { module: EModuleIds.APPROVALS, title: "View Tickets" },
    APPROVALS__ADD_TICKETS: { module: EModuleIds.APPROVALS, title: "Add Tickets" },
    APPROVALS__EDIT_TICKETS: { module: EModuleIds.APPROVALS, title: "Edit Tickets" },
    APPROVALS__VIEW_ADMIN_SWITCH: { module: EModuleIds.APPROVALS, title: "View Admin Switch" },
    APPROVALS__VIEW_DOWNLOAD: { module: EModuleIds.APPROVALS, title: "View Download" },
    APPROVALS__VIEW_OTHER_TICKETS: { module: EModuleIds.APPROVALS, title: "View Other Tickets" },
    APPROVALS__VIEW_HISTORY: { module: EModuleIds.APPROVALS, title: "View History" },
    APPROVALS__EDIT_ACTION: { module: EModuleIds.APPROVALS, title: "Edit Action" },
    APPROVALS__VIEW_TAG: { module: EModuleIds.APPROVALS, title: "View Tag" },
    APPROVALS__MAP_TAG: { module: EModuleIds.APPROVALS, title: "Map Tag" },
    APPROVALS__UNMAP_TAG: { module: EModuleIds.APPROVALS, title: "Unmap Tag" },
    APPROVALS__ADD_TAG: { module: EModuleIds.APPROVALS, title: "Add Tag" },

    // ── Handover & Takeover (module_id: 25) ──────────────────────────────────
    HOTO__VIEW_TICKETS: { module: EModuleIds.HANDOVER_TAKEOVER, title: "View Tickets" },
    HOTO__VIEW_COMMENTS: { module: EModuleIds.HANDOVER_TAKEOVER, title: "View Comments" },
    HOTO__VIEW_TICKET_HISTORY: { module: EModuleIds.HANDOVER_TAKEOVER, title: "View Ticket History" },
    HOTO__ADD_COMMENTS: { module: EModuleIds.HANDOVER_TAKEOVER, title: "Add Comments" },
    HOTO__EDIT_TICKET: { module: EModuleIds.HANDOVER_TAKEOVER, title: "Edit Ticket" },
    HOTO__UPDATE_TICKET_STATUS: { module: EModuleIds.HANDOVER_TAKEOVER, title: "Update ticket status" },
} as const

// ---------------------------------------------------------------------------
// PERMISSION_OVERRIDES (only for loc)
//
// Uncomment any line to force that permission to true/false regardless of
// what the backend returns. Useful for local development and testing.
// Remove overrides before merging to development.
// ---------------------------------------------------------------------------
export const TEST_OVERRIDES: Partial<Record<keyof typeof PERMISSIONS, boolean>> = {
    // These are purposefully set to true do not remove these overrides.
    COMPLAINTS__EDIT_ON_HOLD: true,
    COMPLAINTS__ACCEPT_ASSIGNMENT: true,
    
    // COMPLAINTS__VIEW_ENGINEER_AVAILABILITY: true,
    // ACCOUNTS__LIST_ACCOUNTS: true,
    // FINANCE__EDIT_TICKET: true,
    // PROJECTS__EDIT_TICKET_DETAILS: false,
    // PROJECTS__EDIT_SALES_TAB: false,
    // PROJECTS__VIEW_LINKED_TICKETS: false,
    // SITES__ADD_TICKET: false,
    // SITES__VIEW_VENDOR_BILLS: false,
    // SITES__EDIT_CAPITALISATION_STATUS: false,
    // SURVEY__EDIT_IMAGES: false
}

// ---------------------------------------------------------------------------
// Selector + hook
// ---------------------------------------------------------------------------
type PermissionMap = Record<keyof typeof PERMISSIONS, boolean>

type RoleModule = {
    module_id: number
    data: IAccess[]
    submodule?: { submodule_id: number; data: IAccess[] }[]
}

// Computed once when `role` changes; all components share the same cached result.
export const selectPermissions = createSelector(
    (state: TStore) => state.verifyLogin.loginData?.user_data?.role,
    (role): PermissionMap => {
        const moduleList: RoleModule[] = role ? Object.values(role) : []
        const result = {} as PermissionMap

        for (const key of Object.keys(PERMISSIONS) as Array<keyof typeof PERMISSIONS>) {
            if (key in TEST_OVERRIDES) {
                result[key] = TEST_OVERRIDES[key] as boolean
                continue
            }

            const def = PERMISSIONS[key]
            const moduleData = moduleList.find(m => m.module_id === def.module)

            let permArr: IAccess[] = []
            if ("submodule" in def) {
                permArr = moduleData?.submodule?.find(s => s.submodule_id === (def as any).submodule)?.data ?? []
            } else {
                permArr = moduleData?.data ?? []
            }

            result[key] = permArr.find(p => p.title === def.title)?.value ?? false
        }

        return result
    }
)

const usePermissions = (): PermissionMap => useSelector(selectPermissions)

export default usePermissions
